import { twMerge } from 'tailwind-merge';

import { ViewBy } from '../../../../types/snapshot.types';

export const CAP_TABLE_SCROLL_SELECTOR = '[data-cap-table-h-scroll]';

// No `overscroll-contain` here: the area is a scroll container for the horizontal table scroll and
// the load-more sentinel, so containing overscroll also swallowed wheel events meant for the page
// (the table often has no vertical overflow at all), which froze page scrolling while hovering it.
export const capTableScrollAreaClass = 'max-h-[70vh] w-full min-w-0 overflow-auto';

export const capTableRootClass =
  'w-full shrink-0 border-separate border-spacing-0 bg-white text-nowrap';

export const capTableNestedTableClass =
  'w-full table-fixed border-separate border-spacing-0 bg-brand-25';

export const capTableExpandColumnLayout = {
  colClass: 'box-border w-8 min-w-8 max-w-8',
  nextLeftClass: 'left-8',
} as const;

export const capTableExpandColClass = capTableExpandColumnLayout.colClass;

const headerChromeClass = 'h-11 border-y border-[#F2F2F2] bg-gray-50';
const expandedSurfaceClass = 'bg-brand-25';

/**
 * Carried only by the expanded surface, never by the resting one, so the blue fades *in* alongside
 * the opening panel but is simply gone once the closing one finishes — held on both sides it kept
 * fading for another 300ms after the row had visibly shut.
 *
 * The curve is spelled out rather than `ease-in-out`: Tailwind's is `cubic-bezier(0.4, 0, 0.2, 1)`
 * and framer's `easeInOut`, which drives the height beside it, is `cubic-bezier(0.42, 0, 0.58, 1)`
 * — close enough to look like a mistake rather than a choice. Keep in step with
 * `CAP_TABLE_ROW_EXPAND_MS` / `CAP_TABLE_ROW_EXPAND_EASING`.
 */
export const capTableExpandedSurfaceTransition =
  'transition-colors duration-300 ease-[cubic-bezier(0.42,0,0.58,1)]';

export const getCapTableViewByMode = (viewBy: Record<ViewBy, boolean>): 'all' | ViewBy =>
  viewBy.stakeholders && viewBy.type ? 'all' : viewBy.type ? ViewBy.TYPE : ViewBy.STAKEHOLDERS;

export const hasCapTableExpandColumn = (viewBy: Record<ViewBy, boolean>): boolean =>
  getCapTableViewByMode(viewBy) === 'all';

const stickySurfaceClass = ({
  isExpanded = false,
  isHeader,
}: {
  isExpanded?: boolean;
  isHeader: boolean;
}) =>
  isHeader
    ? twMerge('top-0 z-30', headerChromeClass)
    : twMerge(
        'z-10',
        // The row's own transition cannot reach these cells — a sticky `td` paints its own
        // background — so they carry it themselves, and only while expanded: see
        // `capTableExpandedSurfaceTransition`.
        isExpanded ? twMerge(expandedSurfaceClass, capTableExpandedSurfaceTransition) : 'bg-white',
      );

export const capTableStickyHeaderClass = twMerge('sticky top-0 z-20', headerChromeClass);

export const capTableStickyExpandClass = ({
  isExpanded = false,
  isHeader,
}: {
  isExpanded?: boolean;
  isHeader: boolean;
}) =>
  twMerge(
    'sticky left-0',
    capTableExpandColumnLayout.colClass,
    stickySurfaceClass({ isExpanded, isHeader }),
  );

export const capTableStickyNameClass = ({
  hasExpandColumn,
  isExpanded = false,
  isHeader,
}: {
  hasExpandColumn: boolean;
  isExpanded?: boolean;
  isHeader: boolean;
}) =>
  twMerge(
    'sticky pl-2',
    hasExpandColumn ? capTableExpandColumnLayout.nextLeftClass : 'left-0',
    stickySurfaceClass({ isExpanded, isHeader }),
  );
